import { memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { Tag } from '@/components/ui/tag.js';
import { isLotteryGame } from './lotteryArt.js';
import { useCountdown } from './useCountdown.js';
import { LotteryMedia } from './LotteryMedia.js';
import { lotteryState, playLabelKey, lotteryTimeParts } from './cardMeta.js';
import { formatBangkokDate } from '@/lib/datetime/bangkok.js';
import { ORIGINALS_ART } from '@/features/originals/catalog.js';
import { useGamePromoLock } from '@/features/promo/use-game-lock.js';
import type { LaunchMode, LaunchMethod } from '@/features/games/api.js';
import { parseGameLayers } from '@b1fr0st/contracts/game-layers';
import { LayeredTile } from './LayeredTile.js';
import { popoutHeadroomPx } from './tileArt.js';
import { cardTile, tileImage } from './tileImage.js';
import { BrandBadge } from './BrandBadge.js';
import { badgeCodeOf } from '@/features/games/brandBadge.js';

/**
 * Minimal game shape accepted by GameCard / GameRow.
 * Compatible with both MockGame (mock data) and the real Game type from
 * features/games/api.ts — both carry id, name, gameType, rtp, and tag.
 */
export interface GameLike {
  id: string;
  name: string;
  /** provider-side code (e.g. 'main_th', 'yeekee_64'). Real Game has it. */
  code?: string;
  /** gameType on real API; also on MockGame */
  gameType: string;
  /** RTP: number on real Game, string on MockGame — both rendered as string */
  rtp?: number | string | null;
  /** Measured RTP percent (lobby popular games); preferred over `rtp` and may exceed 100. */
  rtpLive?: number | null;
  /** tag: 'hot'|'new' on real Game, 'Hot'|'New'|'Jackpot' on MockGame */
  tag?: string | null;
  /** providerCode on MockGame; undefined on real Game (use providerDisplayName) */
  providerCode?: string;
  providerDisplayName?: string;
  /** Upstream brand code; with providerCode it picks the logo badge at the top-right. */
  brandCode?: string | null;
  /** providerId — present on real Game, absent on MockGame. Needed to launch. */
  providerId?: string;
  /** Provider launch dispatch — present on real Game from the catalog, lets
   *  the launcher pop a tab synchronously when mode === 'popup'. */
  launchMode?: LaunchMode;
  launchMethod?: LaunchMethod;
  /** Cached banner/logo URL (MinIO) written by the catalog sync. Rendered as
   *  a cover image on non-lottery cards; gradient is the fallback. */
  thumbnailUrl?: string | null;
  /** Image uploaded in BO → Games; the cover when the sync left no thumbnail. */
  imageUrl?: string | null;
  /** Raw `metadata.layers` from the catalog; only `parseGameLayers` decides whether it is drawable. */
  layers?: unknown;
  /** Raw `metadata.tile`, the game's Tile from the art library; only `parseGameTile` decides whether it is drawable. */
  tile?: unknown;
  /** Raw `metadata.themeTiles`, the game's Theme cards by theme code; `cardTile` shows one in place of `tile` inside its window. */
  themeTiles?: unknown;
  /** Lottery fields — present only when category === 'lottery'. */
  category?: string | null;
  lotteryType?: string | null;
  lotteryStatus?: string | null;
  ticketBefore?: string | null;
  ticketAfter?: string | null;
  roundDate?: string | null;
}

function tagToneFor(tag: string | undefined | null): 'hot' | 'new' | 'jackpot' {
  const v = (tag ?? '').toLowerCase();
  if (v === 'jackpot') return 'jackpot';
  if (v === 'hot') return 'hot';
  return 'new';
}

/**
 * Badge text like "95.8% RTP" for any game whose catalog row carries an RTP —
 * Originals get theirs from svc-game's RTP_BY_GAME, third-party camps from
 * whatever the catalog sync or an admin stored. null means "render no badge"
 * (never a "100% RTP" placeholder). The stored unit is a 0–1 fraction, but the
 * backoffice form accepts a bare number, so a value above 1 is read as a
 * percent already; anything outside (0, 100] is treated as garbage.
 */
export function gameRtpBadge(game: GameLike): string | null {
  if (game.rtpLive != null && Number.isFinite(game.rtpLive) && game.rtpLive > 0) {
    return `${Math.round(game.rtpLive * 100) / 100}% RTP`;
  }
  if (game.rtp == null || game.rtp === '') return null;
  const n = Number(game.rtp);
  const pct = n <= 1 ? n * 100 : n;
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
  return `${Math.round(pct * 100) / 100}% RTP`;
}

/** The single cover image: the synced thumbnail, else the one uploaded in BO. */
function gameCoverUrl(game: GameLike): string | null {
  return game.thumbnailUrl ?? game.imageUrl ?? null;
}

// Cover-art fallback gradients — derived from the brand ramp (--art-*, itself
// from --primary/--accent) so placeholder tiles follow the tenant theme, with a
// gentle per-type shift for variety instead of a clashing rainbow.
export const GAME_ART: Record<string, string> = {
  slot:   'var(--art-1)',
  crash:  'var(--art-2)',
  fish:   'var(--art-3)',
  live:   'var(--art-4)',
  table:  'var(--art-5)',
  card:   'var(--art-6)',
  lottery:'var(--art-7)',
  jackpot:'var(--art-4)',
};

// Fallback line-icon per gameType for cards with no cover art.
const TYPE_ICON: Record<string, IconName> = {
  slot: 'cherry', crash: 'trendUp', fish: 'fish', live: 'video',
  table: 'spade', card: 'spade', lottery: 'ticket', other: 'star',
};

export type CardStyle = 'poster' | 'square' | 'wide' | 'minimal' | 'portrait';

/**
 * The "this camp is not in the bonus you are playing" veil.
 *
 * It covers the card rather than hiding it: QA asked for a card the player can
 * still see and still understand — hiding the tile makes a promotion look like
 * an outage. `pointer-events-none` so it never eats the click the card's own
 * `aria-disabled` handler already refuses.
 */
export function PromoLockOverlay({ note }: { note: string }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[3] flex flex-col items-center justify-center gap-1.5 rounded-[inherit] bg-[rgba(5,7,14,.78)] px-3 text-center backdrop-blur-[2px]"
      role="note"
    >
      <Icon name="lock" size={20} className="text-white/85" />
      <span className="text-[11px] font-semibold leading-tight text-white/90 line-clamp-3">
        {note}
      </span>
    </div>
  );
}

interface GameCardProps {
  game: GameLike;
  onPlay?: (game: GameLike) => void;
  launching?: boolean;
  style?: CardStyle;
}

// Memoized — large grids (lottery renders 120 at once) re-render parents on
// every search keystroke and countdown tick; pass a stable onPlay to benefit.
export const GameCard = memo(function GameCard({ game, onPlay, launching = false, style = 'poster' }: GameCardProps) {
  const { t, i18n } = useTranslation(['common', 'lobby']);
  const providerLabel = game.providerDisplayName ?? game.providerCode ?? '';
  const rtpBadge = gameRtpBadge(game);
  // A bonus the player is still working off can forbid this camp or this game
  // type. svc-game refuses such a launch outright, so the card says so instead
  // of opening and then throwing (#434). Resolved here rather than at each of
  // the eight call sites so no surface can render an unlocked copy of a card
  // every other surface locks.
  const { isLocked } = useGamePromoLock();
  const promoLocked = isLocked(game);
  const lockNote = t('lobby:promoLocked.card');
  const blocked = launching || promoLocked;
  const play = () => { if (!blocked) onPlay?.(game); };

  const lottery = isLotteryGame(game);
  const badgeCode = lottery ? null : badgeCodeOf(game);
  const cover = gameCoverUrl(game);
  const layers = useMemo(() => (lottery ? null : parseGameLayers(game.layers)), [lottery, game.layers]);
  // A Tile carries the name in the picture, so a card with one draws neither the
  // name nor the scrim that keeps a drawn name legible. A theme card due now
  // stands in for it; the clock is read per render so a window that opens or
  // closes shows at the next render.
  const tile = lottery ? null : cardTile(game, new Date());
  const tileSrc = tile ? tileImage(tile) : null;
  // A Tile is the whole game already, its layers flattened into it: drawing them
  // again over it would show the hero twice. The card lifts as one instead.
  const cardLayers = tile ? null : layers;
  // Countdown only matters for lottery; pass null otherwise (hook no-ops).
  const remaining = useCountdown(lottery ? (game.ticketBefore ?? null) : null);
  const state = lottery ? lotteryState(game, remaining.expired) : 'closed';
  const open = state === 'open';

  if (style === 'minimal') {
    return (
      <button
        onClick={play}
        disabled={promoLocked}
        title={promoLocked ? lockNote : undefined}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] transition-colors text-left w-full ${promoLocked ? 'opacity-60' : ''}`}
      >
        <div
          className="w-10 h-10 rounded-md shrink-0 relative overflow-hidden"
          style={cover || layers ? undefined : { background: GAME_ART[game.gameType] ?? GAME_ART.slot }}
        >
          {layers ? (
            <LayeredTile layers={layers} label={game.name} radius="0.375rem" still />
          ) : cover && (
            <img
              src={cover}
              alt={game.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {game.tag && (
            <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-[var(--danger)]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[var(--text-primary)] truncate">{game.name}</div>
          <div className="font-mono text-[10px] text-[var(--text-muted)] truncate">
            {lottery ? formatBangkokDate(game.roundDate ?? null, i18n.language) : `${providerLabel}${rtpBadge ? ` · ${rtpBadge}` : ''}`}
          </div>
        </div>
        <Icon
          name={promoLocked ? 'lock' : 'play'}
          size={14}
          className={promoLocked ? 'text-[var(--text-muted)]' : 'text-[var(--primary)]'}
        />
      </button>
    );
  }

  // Duel-style card for every non-lottery game: full-bleed art, RTP badge,
  // name overlaid at the bottom, no provider line, no button — the whole card
  // is the play button. Lottery keeps its dedicated card (countdown/state).
  if (!lottery) {
    // In-house Originals (matched by code) get their canonical icon + gradient
    // so they look identical to the home wheel + navbar dropdown.
    const origArt = game.code ? ORIGINALS_ART[game.code] : undefined;
    const fallbackBg = origArt?.grad ?? GAME_ART[game.gameType] ?? GAME_ART.slot;
    const fallbackIcon = origArt?.icon ?? TYPE_ICON[game.gameType] ?? 'star';
    const overlays = (
      <>
        {/* legibility scrim */}
        {!tile && (
          <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-t from-[rgba(5,7,14,.95)] via-transparent to-[rgba(5,7,14,.2)]" aria-hidden />
        )}

        {/* RTP badge — any game whose catalog row carries an RTP */}
        {rtpBadge && (
          <span className="absolute left-2 top-2 z-[2] rounded-md bg-[rgba(110,231,183,.95)] px-1.5 py-[3px] text-[10px] font-extrabold text-[#04150f]">
            {rtpBadge}
          </span>
        )}

        <BrandBadge code={badgeCode} label={providerLabel} />

        {game.tag && (
          <Tag tone={tagToneFor(game.tag)} className={`absolute right-2 ${badgeCode ? 'top-[38px]' : 'top-2'} z-[2] shadow-[0_2px_8px_rgba(0,0,0,.4)]`}>
            {String(game.tag).toUpperCase()}
          </Tag>
        )}

        {/* name */}
        {!tile && (
          <div className="absolute inset-x-0 bottom-0 z-[2] px-2.5 pb-2.5 pt-6">
            <div className="text-[13px] font-extrabold leading-tight text-white line-clamp-2 drop-shadow-[0_2px_6px_rgba(0,0,0,.6)]" title={game.name}>
              {game.name}
            </div>
          </div>
        )}

        {promoLocked && <PromoLockOverlay note={lockNote} />}
      </>
    );
    return (
      <Card
        hoverable={!promoLocked}
        onClick={play}
        aria-disabled={promoLocked || undefined}
        // A layered tile tilts and lifts itself, and its frame paints the
        // background so the card's own never shows behind a tilted corner.
        className={`group relative aspect-[3/4] rounded-2xl !border-0 ${cardLayers ? '!bg-transparent' : '!bg-[#07060f]'} transition-transform duration-200 ease-out ${
          promoLocked
            ? 'cursor-not-allowed'
            : `cursor-pointer hover:z-10${cardLayers ? '' : ' hover:scale-[1.04]'}`
        } ${launching ? 'opacity-60 pointer-events-none' : ''}`}
      >
        {cardLayers ? (
          <LayeredTile layers={cardLayers} label={game.name} radius="1rem" still={promoLocked}>
            {overlays}
          </LayeredTile>
        ) : tileSrc ? (
          <>
            {/* Rounded itself rather than clipped by the card, so no hairline
                seam shows at the corners (see the stand-in branch below). */}
            <img
              data-game-tile
              src={tileSrc.src}
              srcSet={tileSrc.srcSet}
              alt={game.name}
              loading="lazy"
              decoding="async"
              draggable={false}
              className="absolute inset-0 h-full w-full rounded-[inherit] object-cover"
            />
            {overlays}
          </>
        ) : (
          <>
            {/* Art painted as a background on the rounded layer — not a clipped <img> —
                so Chrome never leaves a light hairline seam at the rounded corners
                (overflow-clip + border-radius is what produced it). */}
            <div
              className="absolute inset-0 rounded-[inherit] bg-cover bg-center"
              role={cover ? 'img' : undefined}
              aria-label={cover ? game.name : undefined}
              style={{ backgroundImage: cover ? `url("${cover}")` : fallbackBg }}
            >
              {!cover && (
                <span className="absolute inset-0 grid place-items-center">
                  <Icon name={fallbackIcon} size={46} className="text-white/90 drop-shadow-[0_4px_12px_rgba(0,0,0,.5)]" />
                </span>
              )}
            </div>
            {overlays}
          </>
        )}
      </Card>
    );
  }

  const aspectClass =
    style === 'square' ? 'aspect-square' :
    style === 'wide'   ? 'aspect-[16/9]' : 'aspect-[8/5]';

  const times = lottery ? lotteryTimeParts(game) : null;
  const playKey = playLabelKey(game, open);

  return (
    <Card
      hoverable={!promoLocked}
      aria-disabled={promoLocked || undefined}
      className={`relative overflow-hidden flex flex-col group ${
        promoLocked ? 'cursor-not-allowed' : 'cursor-pointer'
      } ${launching ? 'opacity-70' : ''}`}
      onClick={play}
    >
      <div
        className={`relative ${aspectClass} w-full overflow-hidden flex items-center justify-center text-white font-bold text-sm px-3 text-center transition-transform duration-200 group-hover:scale-[1.03]`}
        style={lottery ? undefined : (cover ? undefined : { background: GAME_ART[game.gameType] ?? GAME_ART.slot })}
      >
        {lottery ? (
          <LotteryMedia game={game} remaining={remaining} state={state} t={t} />
        ) : (
          <>
            {cover ? (
              <>
                <img
                  src={cover}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60"
                />
                <img
                  src={cover}
                  alt={game.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain [mask-image:linear-gradient(to_right,transparent,#000_16%,#000_84%,transparent),linear-gradient(to_bottom,transparent,#000_16%,#000_84%,transparent)] [mask-composite:intersect] [-webkit-mask-image:linear-gradient(to_right,transparent,#000_16%,#000_84%,transparent),linear-gradient(to_bottom,transparent,#000_16%,#000_84%,transparent)] [-webkit-mask-composite:source-in]"
                />
              </>
            ) : (
              <span className="line-clamp-2 drop-shadow-[0_2px_4px_rgba(0,0,0,.5)]">{game.name}</span>
            )}
            <BrandBadge code={badgeCode} label={providerLabel} />
            {game.tag && (
              <Tag
                tone={tagToneFor(game.tag)}
                className={`absolute ${badgeCode ? 'top-[38px]' : 'top-2'} right-2 shadow-[0_2px_8px_rgba(0,0,0,.4)]`}
              >
                {String(game.tag).toUpperCase()}
              </Tag>
            )}
          </>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5">
        <h3
          className="text-[13px] font-semibold leading-tight text-[var(--text-primary)] line-clamp-1"
          title={game.name}
        >
          {game.name}
        </h3>
        <div className="flex items-center justify-between gap-2 font-mono text-[10px] text-[var(--text-muted)] min-h-[14px]">
          {lottery && times ? (
            <span className="truncate">
              {times.openAt && `${t('lottery.open')} ${times.openAt}`}
              {times.openAt && times.closeAt && ' · '}
              {times.closeAt && `${t('lottery.closed')} ${times.closeAt}`}
              {!times.openAt && !times.closeAt && formatBangkokDate(game.roundDate ?? null, i18n.language)}
            </span>
          ) : (
            <>
              <span className="truncate">{providerLabel}</span>
              {rtpBadge && <span className="shrink-0">{rtpBadge}</span>}
            </>
          )}
        </div>
        <Button
          size="sm"
          fullWidth
          disabled={blocked}
          onClick={(e) => { e.stopPropagation(); play(); }}
        >
          {launching ? '…' : promoLocked ? (
            <><Icon name="lock" size={12} /> {t('lobby:promoLocked.button')}</>
          ) : (
            <><Icon name="play" size={12} /> {t(playKey)}</>
          )}
        </Button>
      </div>

      {promoLocked && <PromoLockOverlay note={lockNote} />}
    </Card>
  );
});

// ---- GameRow ----

interface GameRowProps {
  title: string;
  accentColor?: string;
  icon?: Parameters<typeof Icon>[0]['name'];
  games: GameLike[];
  onPlay?: (game: GameLike) => void;
  onAll?: () => void;
  cardStyle?: CardStyle;
}

function cardWidthPx(style: CardStyle): number {
  const base: Record<CardStyle, number> = {
    poster:  184,
    square:  144,
    wide:    248,
    minimal: 280,
    portrait: 150,
  };
  return base[style];
}

export function GameRow({ title, accentColor, icon, games, onPlay, onAll, cardStyle = 'poster' }: GameRowProps) {
  const { t } = useTranslation('common');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardW = cardWidthPx(cardStyle);
  const headroom = useMemo(() => (cardStyle === 'minimal' ? 0 : popoutHeadroomPx(games, cardW, new Date())), [games, cardStyle, cardW]);

  function scrollBy(dir: number) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  }

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {accentColor && (
            <span className="w-1 h-5 rounded-full" style={{ background: `var(${accentColor})` }} />
          )}
          {icon && <Icon name={icon} size={18} className="text-[var(--text-secondary)]" />}
          <h2 className="text-[17px] md:text-xl font-bold tracking-tight truncate">{title}</h2>
          <span className="font-mono text-[11px] text-[var(--text-muted)] hidden sm:inline">{games.length}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => scrollBy(-1)}
            className="w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] grid place-items-center text-[var(--text-secondary)] transition-colors"
            aria-label="Scroll left"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] grid place-items-center text-[var(--text-secondary)] transition-colors"
            aria-label="Scroll right"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          {onAll && (
            <button
              onClick={onAll}
              className="ml-1 h-8 px-3 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] text-[12px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t('common.viewAll')} →
            </button>
          )}
        </div>
      </div>
      {/* A popout tile needs room above it, or the scroller clips the art that
          breaks the frame. The room is pulled back up over the header with an
          equal negative margin, so the cards sit where every other row's do;
          the scroller ignores the pointer there so the header stays clickable. */}
      <div
        ref={scrollerRef}
        data-popout-headroom={headroom || undefined}
        className={`flex gap-3 overflow-x-auto overflow-y-clip bf-noscroll snap-x snap-mandatory -mx-4 px-4 py-2.5 -my-2.5 md:mx-0 md:px-0${
          headroom ? ' pointer-events-none [&>*]:pointer-events-auto' : ''
        }`}
        style={{
          scrollPaddingLeft: '1rem',
          ...(headroom
            ? { paddingTop: `calc(0.625rem + ${headroom}px)`, marginTop: `calc(-0.625rem - ${headroom}px)` }
            : {}),
        }}
      >
        {games.map((g) => (
          <div key={g.id} className="shrink-0 snap-start" style={{ width: cardW }}>
            <GameCard game={g} onPlay={onPlay} style={cardStyle} />
          </div>
        ))}
        {onAll && (
          <button
            onClick={onAll}
            className="shrink-0 snap-start flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--border-strong)] bg-[var(--bg-raised)]/40 hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            style={{ width: Math.round(cardW * 0.7), minHeight: 120 }}
          >
            <div className="w-10 h-10 rounded-full bg-[var(--bg-elevated)] grid place-items-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </div>
            <span className="text-[12px] font-semibold">{t('common.viewAll')}</span>
          </button>
        )}
      </div>
    </section>
  );
}
